<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";
import Check from "@/design/primitives/Check.vue";
import SelectField from "@/design/primitives/SelectField.vue";
import { OPTION_SOURCE_PORT, type SourcedOption } from "@/shared/contracts/ports";
import { displayOf, optionsFor } from "../resolvers/field-config";
import { optionSourceOf } from "../resolvers/option-source";
import { say } from "../say";
import type { WidgetProps } from "./contract";

/**
 * A single choice, as a dropdown or as radio buttons.
 *
 * Radio is `config.display`, never a separate field type (ADR-F3). The stored
 * value is the same string either way, so switching the appearance is a metadata
 * edit and not a migration.
 *
 * ── E AS OPÇÕES PODEM VIR DE UMA CONSULTA ─────────────────────────────────
 *
 * `config.source` aponta para um artefato `query` do tenant (§3.11): os horários
 * livres de um dia, os lotes com saldo. As linhas chegam pela porta injetada e
 * nunca por HTTP daqui — `dynamic/` é dirigido por metadado, e a regra que proíbe
 * o atalho é a mesma que faz este renderizador servir toda tela sem conhecer
 * nenhuma.
 *
 * A frase abaixo do campo não é enfeite: uma seleção vazia e calada não distingue
 * "hoje não há horário" de "a consulta quebrou", e quem está preenchendo é a única
 * pessoa que não pode descobrir a diferença sozinha.
 */
const props = defineProps<WidgetProps & { controlId?: string; describedBy?: string }>();
const emit = defineEmits<{ "update:modelValue": [unknown]; blur: [] }>();

const port = inject(OPTION_SOURCE_PORT, null);

const source = computed(() => optionSourceOf(props.field.config));
const sourced = ref<SourcedOption[]>([]);
const notice = ref("");
const loading = ref(false);

watch(
	source,
	async (asked) => {
		if (!asked) {
			sourced.value = [];
			notice.value = "";
			return;
		}
		if (!port) {
			// Um formulário público não provê esta porta. Dizê-lo é melhor do que
			// oferecer uma caixa vazia que parece uma pergunta sem resposta possível.
			sourced.value = [];
			notice.value = say("optionsUnavailable");
			return;
		}
		loading.value = true;
		try {
			const answer = await port.options(asked);
			sourced.value = [...answer.options];
			notice.value = answer.notice ?? "";
		} finally {
			loading.value = false;
		}
	},
	{ immediate: true, deep: true },
);

const options = computed(() =>
	source.value
		? sourced.value.map((option) => ({ value: option.value, label: option.label }))
		: optionsFor(props.field.config),
);
const message = computed(() => (loading.value ? say("loadingOptions") : notice.value));
const asRadio = computed(() => displayOf(props.field.config) === "radio");
const current = computed(() =>
	props.modelValue === null || props.modelValue === undefined ? "" : String(props.modelValue),
);
</script>

<template>
	<div
		v-if="asRadio"
		:id="controlId"
		class="space-y-1.5"
		role="radiogroup"
		:aria-label="field.label"
		:aria-describedby="describedBy"
	>
		<Check
			v-for="option in options"
			:key="option.value"
			type="radio"
			:name="field.code"
			:value="option.value"
			:label="option.label"
			:model-value="current === option.value"
			:disabled="disabled"
			@update:model-value="emit('update:modelValue', option.value)"
		/>
		<!-- Optional fields need a way OUT of a radio group; without this the first
		     accidental click can never be undone. -->
		<button
			v-if="!field.required && current"
			type="button"
			class="text-xs text-fg-subtle underline"
			@click="emit('update:modelValue', null)"
		>
			limpar
		</button>
		<p v-if="message" class="text-xs text-fg-subtle">{{ message }}</p>
	</div>

	<!-- Envolvido num div para a frase caber embaixo do campo: um segundo nó raiz
	     ao lado do controle faria os atributos herdados do formulário perderem o
	     destino, e o aviso do Vue sobre isso é o sintoma, não o problema. -->
	<div v-else>
		<SelectField
			:id="controlId"
			:model-value="current"
			:options="options"
			placeholder="—"
			:disabled="disabled"
			:invalid="Boolean(error)"
			:described-by="describedBy"
			@update:model-value="emit('update:modelValue', $event || null)"
			@blur="emit('blur')"
		/>
		<p v-if="message" class="mt-1 text-xs text-fg-subtle">{{ message }}</p>
	</div>
</template>
